<template>
  <div v-if="selectedriskClasses.length > 0" class="sub-category-section-margin">
    <div
      v-for="(riskType, index) in selectedriskClasses[0].types"
      :key="index"
      class="sub-category-section"
    >
      <v-row v-if="riskType.classes.length > 0" no-gutters>
        <v-col v-if="riskTypeLabelDisplayed">
          <div class="name pl-0 pt-3">{{ riskType.name }}</div>
        </v-col>
        <v-col v-if="selectedriskClasses[0].name === 'Activities'">
          <complete-checklist
            :category-name="riskType.name"
            :risk-class-name="selectedriskClasses[0].name"
            :form-control-enabled="formControlEnabled"
            :checklist-fetch-action-in-progress="false"
          />
          <view-policy
            :category-name="riskType.name"
            :risk-class-name="selectedriskClasses[0].name"
          />
        </v-col>
      </v-row>
      <v-row
        v-for="(selectedRiskClass, classIndex) in riskType.classes"
        :key="classIndex"
        no-gutters
        align="center"
      >
        <v-col cols="11">
          <v-select
            :id="getRiskClassId(selectedRiskClass)"
            class="type-dropdown risk-class-select excursion-selection py-1"
            :items="
              listRiskClasses(selectedRiskClass.name, riskType.name, selectedriskClasses[0].name)
            "
            :item-props="
              isSelectedRiskClassHighRiskMedicalCondition ? listRiskClassesProps : 'props'
            "
            color="primary"
            variant="solo"
            density="compact"
            hide-details
            :model-value="selectedRiskClass.name"
            :disabled="
              !selectedClassExistInItems(
                selectedRiskClass.name,
                riskType.name,
                selectedriskClasses[0].name
              ) || !formControlEnabled
            "
            @click="showWarning"
            @update:model-value="
              changeSelectedClassId(
                riskType.name,
                selectedriskClasses[0].name,
                selectedRiskClass,
                $event
              )
            "
          >
            <template #item="data">
              <v-list-item
                v-bind="data.props"
                :id="`risk-class--option-${String(data.props.title).replaceAll(' ', '')}`"
                :disabled="getDisabledState(data, riskType)"
                active-class="active-risk-class"
                :class="{
                  'disabled-risk': getDisabledState(data, riskType)
                }"
              />
            </template>
          </v-select>
        </v-col>
        <v-col cols="1" class="d-flex justify-center mb-2">
          <v-icon
            :id="getRiskClassDeleteId(selectedRiskClass)"
            :disabled="!formControlEnabled"
            size="large"
            @click="
              openRemoveSelectedClassDialog(
                riskType.name,
                selectedriskClasses[0].name,
                selectedRiskClass.name
              )
            "
          >
            mdi-delete
          </v-icon>
        </v-col>
        <cs-form-dialog
          id="dlg-selected-risk-class"
          v-model="selectedRiskClass.dialog"
          body-id="dlg-body-selected-risk-class"
          heading="Delete Risk"
          :primary-action="{
            label: 'Delete',
            eventName: 'primary-click',
            disabled: !formControlEnabled
          }"
          :secondary-action1="{
            label: 'Cancel',
            eventName: 'secondary1-click'
          }"
          @primary-click="
            removeSelectedClassId(riskType.name, selectedriskClasses[0].name, selectedRiskClass)
          "
          @secondary1-click="
            closeRemoveSelectedClassDialog(
              riskType.name,
              selectedriskClasses[0].name,
              selectedRiskClass.name
            )
          "
          @update:model-value="
            closeRemoveSelectedClassDialog(
              riskType.name,
              selectedriskClasses[0].name,
              selectedRiskClass.name
            )
          "
        >
          <template #cs-form-dialog-content>
            <v-card variant="flat">
              <v-card-text v-if="isSelectedRiskClassHighRiskMedicalCondition"
                >This action may populate a new checklist. Do you want to continue?
              </v-card-text>
              <v-card-text v-else>Are you sure?</v-card-text>
            </v-card>
          </template>
        </cs-form-dialog>
      </v-row>
    </div>
    <cs-form-dialog
      id="dlg-update-risk-class-warning"
      v-model="warningUpdateRiskClassDialogDisplayed"
      body-id="dlg-body-update-risk-class-warning"
      heading="Update Student Medical Condition"
      :primary-action="{
        label: 'Confirm',
        eventName: 'primary-click'
      }"
      :secondary-action1="{
        label: 'Cancel',
        eventName: 'secondary1-click'
      }"
      @primary-click="closeWarningDialog"
      @secondary1-click="warningUpdateRiskClassDialogDisplayed = false"
      @update:model-value="warningUpdateRiskClassDialogDisplayed = false"
    >
      <template #cs-form-dialog-content>
        This action may populate a new checklist. Do you want to continue?
      </template>
    </cs-form-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { find } from 'lodash';
import riskCategoryLabel from '@/lib/const/riskCategoryLabel';
import CompleteChecklist from './CompleteChecklist';

export default {
  name: 'RiskClass',
  components: {
    CompleteChecklist
  },
  props: {
    selectedriskClasses: {
      type: Array,
      required: false,
      default: () => []
    },
    formControlEnabled: {
      type: Boolean,
      required: true
    },
    riskTypeLabelDisplayed: {
      type: Boolean,
      required: false,
      default: false
    },
    hasUpdatedMedicalCondition: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['hasUpdatedMedicalCondition', 'changeRiskCategoryDone'],
  data() {
    return {
      warningUpdateRiskClassDialogDisplayed: false,
      riskCategoryMap: {
        Transport: 'transports',
        Accommodation: 'accommodations',
        'Food and Beverage': 'foods',
        Venues: 'venues'
      }
    };
  },
  computed: {
    ...mapState({
      excursionRiskCategories: (state) => state.excursions.riskCategoriesOptions
    }),
    riskCategories() {
      const categoryName = this.selectedriskClasses[0].name;
      const propertyName = this.riskCategoryMap[categoryName];
      return this.excursionRiskCategories[propertyName] || [];
    },
    placeholder() {
      return `Add ${this.selectedriskClasses[0].name} Type`;
    },
    allRisks() {
      return this.$store.state.common.allRisks;
    },
    selectedRisks() {
      const { categories } = this.$store.state.common.excursionDc;
      if (categories) {
        return this.$store.state.common.excursionDc.categories;
      }
      return [];
    },
    isSelectedRiskClassHighRiskMedicalCondition() {
      return (
        this.selectedriskClasses?.[0].name ===
        riskCategoryLabel.HIGH_RISK_STUDENT_MEDICAL_CONDITIONS
      );
    },
    getDisabledState() {
      return (data, riskType) => {
        if (this.isSelectedRiskClassHighRiskMedicalCondition) {
          return data.props.disabled;
        }
        const selectedRiskClasses = riskType.classes;
        const optionName = data.props.title;
        return selectedRiskClasses.some((riskClass) => riskClass.name === optionName);
      };
    }
  },
  methods: {
    showWarning(event) {
      if (!this.hasUpdatedMedicalCondition && this.isSelectedRiskClassHighRiskMedicalCondition) {
        event.stopPropagation();
        this.warningUpdateRiskClassDialogDisplayed = true;
      }
    },
    closeWarningDialog() {
      this.$emit('hasUpdatedMedicalCondition', true);
      this.warningUpdateRiskClassDialogDisplayed = false;
    },
    listRiskClasses(selectedClassName, typeName, categoryName) {
      let classes = [];
      if (this.isSelectedRiskClassHighRiskMedicalCondition) {
        Object.keys(this.allRisks).forEach((categoryKey) => {
          if (this.allRisks[categoryKey].name === categoryName) {
            Object.keys(this.allRisks[categoryKey].types).forEach((typeKey) => {
              if (this.allRisks[categoryKey].types[typeKey].name === typeName) {
                classes = this.allRisks[categoryKey].types[typeKey].classes;
              }
            });
          }
        });
        if (selectedClassName) {
          if (
            classes.length === 0 ||
            classes.findIndex((c) => c.name === selectedClassName) === -1
          ) {
            return [{ name: selectedClassName }];
          }
        }
        return classes;
      } else {
        const result = find(this.riskCategories, { name: typeName });

        if (
          selectedClassName &&
          (result?.options.length === 0 ||
            !result?.options.find((item) => item === selectedClassName))
        ) {
          return [selectedClassName];
        }

        return result?.options || [];
      }
    },
    listRiskClassesProps(item) {
      return {
        title: item.name,
        value: item.name,
        disabled: item.disabled
      };
    },
    getClassFromAllRisks(typeName, categoryName, className) {
      let foundClass;
      Object.keys(this.allRisks).forEach((categoryKey) => {
        if (this.allRisks[categoryKey].name === categoryName) {
          Object.keys(this.allRisks[categoryKey].types).forEach((typeKey) => {
            if (this.allRisks[categoryKey].types[typeKey].name === typeName) {
              foundClass = this.allRisks[categoryKey].types[typeKey].classes.find(
                (c) => c.name === className
              );
            }
          });
        }
      });
      return foundClass;
    },
    getRiskClassId(riskClass) {
      return `risk-class--${riskClass.name.replace(/\s/g, '')}`;
    },
    getRiskClassDeleteId(riskClass) {
      return `risk-class--delete-${riskClass.name.replace(/\s/g, '')}`;
    },
    changeSelectedClassId(typeName, categoryName, oldRiskClass, newRiskClassName) {
      if (this.isSelectedRiskClassHighRiskMedicalCondition) {
        this.$store.dispatch('common/setRiskClassDisabled', {
          typeName,
          categoryName,
          className: newRiskClassName,
          disabled: true
        });
        this.$store.dispatch('common/setRiskClassDisabled', {
          typeName,
          categoryName,
          className: oldRiskClass.name,
          disabled: false
        });
      }
      this.$store.dispatch('common/setExcursionSelectedCategoryTypeClass', {
        typeName,
        categoryName,
        oldClassName: oldRiskClass.name,
        newClassName: newRiskClassName
      });
      this.$emit('changeRiskCategoryDone');
    },
    openRemoveSelectedClassDialog(typeName, categoryName, className) {
      this.$store.dispatch('common/setExcursionSelectedCategoryTypeClassBoolDialog', {
        categoryName,
        typeName,
        className,
        boolDialog: true
      });
    },
    closeRemoveSelectedClassDialog(typeName, categoryName, className) {
      this.$store.dispatch('common/setExcursionSelectedCategoryTypeClassBoolDialog', {
        categoryName,
        typeName,
        className,
        boolDialog: false
      });
    },
    removeSelectedClassId(typeName, categoryName, selectedRiskClass) {
      if (!selectedRiskClass.dialog) {
        return false;
      }
      if (this.isSelectedRiskClassHighRiskMedicalCondition) {
        this.$store.dispatch('common/setRiskClassDisabled', {
          typeName,
          categoryName,
          className: selectedRiskClass.name,
          disabled: false
        });
      }
      this.$store.dispatch('common/deleteExcursionSelectedCategoryTypeClass', {
        categoryName,
        typeName,
        className: selectedRiskClass.name
      });
      this.$emit('changeRiskCategoryDone');
      return true;
    },
    selectedClassExistInItems(selectedClassName, typeName, categoryName) {
      const items = this.listRiskClasses('', typeName, categoryName);
      if (this.isSelectedRiskClassHighRiskMedicalCondition) {
        return items.findIndex((i) => i.name === selectedClassName) > -1;
      }
      return items.findIndex((item) => item === selectedClassName) > -1;
    }
  }
};
</script>

<style scoped>
:deep(.risk-class-select .v-select__selection-text) {
  color: rgb(var(--v-theme-black-lighten-5));
}

.active-risk-class :deep(.v-list-item__overlay) {
  background-color: #ffffff;
}

.active-risk-class.disabled-risk {
  opacity: 1;
}

.disabled-risk {
  opacity: 0.4;
}
</style>
